import { useState } from 'react';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import SelectDialectControl from 'components/SelectDialectControl';
import {
  DetailsHeader,
  SvgAnalyticsBarsBold,
  TextV2,
} from '@insights-ltd/design-library/components';
import isPronounSupportedByDialect from 'utils/isPronounSupportedByDialect';
import { LearnerEvaluation, ProfileResponse } from 'api';
import {
  DIALECT_CODES,
  ENABLED_EVALUATOR_DIALECTS,
  getDialect,
} from 'variables';
import { AllDialectOptions, Dialects } from 'types/dialects';
import Notification from 'components/Notification';
import { Evaluator } from 'types/types';
import { useBetaEnabled } from 'features';
import { PurchaseCard } from './PurchaseCard';
import { PersonalImpactProfileCard, ProfileCard } from './ProfileCards';

type ProfilesProps = {
  learnerId: string;
  profiles: ProfileResponse[];
  onProfileDownloadError: () => void;
  evaluatorSubmittedOn?: string;
  organisationId: string;
  latestEvaluation?: LearnerEvaluation;
};

const nonePronounEvaluators: Evaluator[] = ['CLASSIC_EXPLORE', 'EXPLORE'];

const pronounSupportedProfile = (
  profile: ProfileResponse,
  dialect: AllDialectOptions,
) => {
  return (
    nonePronounEvaluators.includes(profile.evaluatorType) ||
    isPronounSupportedByDialect({
      pronoun: profile.pronoun,
      dialect,
    })
  );
};

export const profileDownloadable = (
  profile: ProfileResponse,
  dialect: AllDialectOptions,
) => {
  const supportedDialects = profile.supportedDialects;

  if (!supportedDialects || supportedDialects.length === 0) {
    return false;
  }

  if (!profile?.dialect) {
    return false;
  }

  const dialectConfig = getDialect(profile.dialect);

  if (dialect === DIALECT_CODES.EVALUATION_DIALECT && !dialectConfig) {
    return false;
  }

  if (
    dialect === DIALECT_CODES.EVALUATION_DIALECT &&
    supportedDialects.includes(profile.dialect as Dialects)
  ) {
    return true;
  }
  if (
    dialect === DIALECT_CODES.EVALUATION_DIALECT &&
    !supportedDialects.includes(profile.dialect)
  ) {
    return false;
  }
  if (supportedDialects.includes(dialect as Dialects)) {
    return true;
  }

  if (
    dialect === DIALECT_CODES.EVALUATION_DIALECT &&
    dialectConfig?.disabledForProfiles &&
    !dialectConfig.disabledForProfiles.includes(profile.type)
  ) {
    return true;
  }
  if (
    dialect === DIALECT_CODES.EVALUATION_DIALECT &&
    dialectConfig?.disabledForProfiles &&
    dialectConfig.disabledForProfiles.includes(profile.type)
  ) {
    return false;
  }

  if (
    dialect !== DIALECT_CODES.EVALUATION_DIALECT &&
    dialectConfig?.disabledForProfiles &&
    dialectConfig.disabledForProfiles.includes(profile.type)
  ) {
    return false;
  }
  if (
    dialect !== DIALECT_CODES.EVALUATION_DIALECT &&
    !supportedDialects.includes(dialect as Dialects)
  ) {
    return false;
  }

  return pronounSupportedProfile(profile, dialect);
};

const Profiles = ({
  profiles,
  learnerId,
  onProfileDownloadError,
  evaluatorSubmittedOn,
  organisationId,
  latestEvaluation,
}: ProfilesProps) => {
  const { t } = useTranslation();
  const personalImpactBetaEnabled = useBetaEnabled('personal-impact-profile');
  const [selectedDialect, setSelectedDialect] = useState<AllDialectOptions>(
    DIALECT_CODES.EVALUATION_DIALECT as Dialects,
  );

  const handleDialectSelection = (selection: AllDialectOptions) => {
    setSelectedDialect(selection);
  };

  const normalizedProfiles = profiles.map((profile) => ({
    ...profile,
  }));

  const profileCount = normalizedProfiles.length;
  const disabledProfiles = normalizedProfiles.filter(
    (profile) => !profileDownloadable(profile, selectedDialect),
  );
  const dialectString =
    selectedDialect === DIALECT_CODES.EVALUATION_DIALECT
      ? undefined
      : ENABLED_EVALUATOR_DIALECTS.find((dl) => dl.value === selectedDialect)
          ?.textKey;

  const latestEvaluationProfiles = normalizedProfiles.filter(
    ({ evaluation }) => evaluation.id === latestEvaluation?.id,
  );

  return (
    <Grid
      container
      spacing={2}
      rowSpacing={1}
      sx={{
        mt: -1,
      }}
    >
      <Grid
        container
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          mx: -1,
          width: 'calc(100% + 16px)',
        }}
        size={12}
      >
        <DetailsHeader
          Icon={SvgAnalyticsBarsBold}
          text={t('ui.event-management.learner.profiles', { profileCount })}
          fullWidth
          id="learner-profiles-info"
        />
      </Grid>
      <Grid container spacing={2} size={12}>
        {profileCount > 0 ? (
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextV2 variant="h4">
                {t('ui.event-management.distribute-profiles.dialect-label')}
              </TextV2>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SelectDialectControl
                options={ENABLED_EVALUATOR_DIALECTS}
                dialect={selectedDialect}
                setDialect={handleDialectSelection}
                fullWidth
              />
            </Grid>
          </Grid>
        ) : null}
        {disabledProfiles.length > 0 && (
          <Grid size={12}>
            <Notification
              message={t(
                'ui.event-management.learner.purchase.download-warning',
              )}
            />
          </Grid>
        )}
        <Grid container spacing={2} data-testid="profiles" size={12}>
          {evaluatorSubmittedOn ? (
            <Grid key="purchase" size={{ xs: 12, sm: 4 }}>
              <PurchaseCard
                profileExists={profileCount > 0}
                evaluatorCompletedAt={evaluatorSubmittedOn}
                disabled={isClassicExplore(
                  normalizedProfiles,
                  evaluatorSubmittedOn,
                )}
                organisationId={organisationId}
                latestEvaluation={latestEvaluation}
                learnerId={learnerId}
                profiles={latestEvaluationProfiles}
                isInsightsExplore={isClassicExplore(
                  normalizedProfiles,
                  evaluatorSubmittedOn,
                )}
              />
            </Grid>
          ) : null}
          {normalizedProfiles.map((profile) => {
            if (
              profile.type === 'IMPACT_PROFILE' &&
              !personalImpactBetaEnabled
            ) {
              return null;
            }

            return (
              <Grid key={profile.id} size={{ xs: 12, sm: 4 }}>
                {profile.type === 'IMPACT_PROFILE' ? (
                  <PersonalImpactProfileCard
                    learnerId={learnerId}
                    profile={profile}
                    disabled={!profileDownloadable(profile, selectedDialect)}
                    selectedDialect={selectedDialect}
                    dialectString={dialectString}
                    onProfileDownloadError={onProfileDownloadError}
                    organisationId={organisationId}
                  />
                ) : (
                  <ProfileCard
                    learnerId={learnerId}
                    profile={profile}
                    disabled={!profileDownloadable(profile, selectedDialect)}
                    selectedDialect={selectedDialect}
                    dialectString={dialectString}
                    onProfileDownloadError={onProfileDownloadError}
                    organisationId={organisationId}
                  />
                )}
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Profiles;

function isClassicExplore(
  profiles: ProfileResponse[],
  latestEvaluatorSubmittedOn: string | undefined,
) {
  return (
    profiles.find((e) => e.evaluationSubmittedOn === latestEvaluatorSubmittedOn)
      ?.evaluatorType === 'CLASSIC_EXPLORE'
  );
}
